import { Box, Group, Image, ScrollArea, SimpleGrid, Stack, Table, Text } from "@mantine/core"
import { PackageIcon } from "@phosphor-icons/react"
import { useTranslation } from "react-i18next"
import { DnDetail, formatMoney } from "./dn-detail-utils"
import { DnSectionTitle } from "./dn-overview"
import classes from "./dn-detail.module.css"

interface LineLike {
  id?: string
  name?: string | null
  brand?: string | null
  skuCode?: string | null
  image?: string | null
  quantity?: number | null
  price?: number | null
  totalAmount?: number | null
  sizeValue?: string | null
  weight?: number | null
}

function Tile({ label, value }: Readonly<{ label: string; value: string | number }>) {
  return (
    <Box className={classes.tile}>
      <Text className={classes.label}>{label}</Text>
      <Text className={classes.tileValue}>{value}</Text>
    </Box>
  )
}

/** «Хүргэх бараа»: lines table + 4 summary tiles (pieces, weight, effective size, order amount). */
export function DnLines({ dn }: Readonly<{ dn: DnDetail }>) {
  const { t } = useTranslation("delivery")
  const lines = (dn.lines ?? []) as unknown as LineLike[]
  const totalItemCount = dn.totalItemCount ?? lines.reduce((s, l) => s + (l.quantity ?? 0), 0)
  const totalWeight = lines.reduce((s, l) => s + (l.weight ?? 0) * (l.quantity ?? 0), 0)
  const kg = (n: number) => t("dnDetail.kg", { n })

  return (
    <Stack gap="md">
      <DnSectionTitle icon={<PackageIcon size={18} />} title={t("dnDetail.itemsHeader", { names: lines.length, count: totalItemCount })} />
      {lines.length > 0 ? (
        <Box className={classes.tableFrame}>
          <ScrollArea>
            <Table layout="fixed" verticalSpacing="sm" horizontalSpacing="md" miw={720}>
              <Table.Thead bg="var(--nw-table-head-bg)">
                <Table.Tr className={classes.noWrap}>
                  <Table.Th>{t("dnDetail.colProduct")}</Table.Th>
                  <Table.Th w={130}>{t("dnDetail.colSku")}</Table.Th>
                  <Table.Th w={64}>{t("dnDetail.colSize")}</Table.Th>
                  <Table.Th w={48} ta="right">{t("dnDetail.colQty")}</Table.Th>
                  <Table.Th w={84} ta="right">{t("dnDetail.colWeight")}</Table.Th>
                  <Table.Th w={100} ta="right">{t("dnDetail.colUnitPrice")}</Table.Th>
                  <Table.Th w={104} ta="right">{t("dnDetail.colTotal")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {lines.map((l, i) => (
                  <Table.Tr key={l.id ?? i}>
                    <Table.Td>
                      <Group gap="sm" wrap="nowrap">
                        <Box className={classes.thumb}>
                          {l.image ? <Image src={l.image} alt={l.name ?? ""} w={40} h={40} fit="cover" /> : <PackageIcon size={20} />}
                        </Box>
                        <Stack gap={0} miw={0}>
                          <Text fz="var(--nw-fz-body)" fw={650}>
                            {l.name || "-"}
                          </Text>
                          {l.brand && <Text className={classes.label}>{t("dnDetail.brandPrefix", { brand: l.brand })}</Text>}
                        </Stack>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text ff="monospace" fz="var(--nw-fz-mono)" c="var(--nw-text-2)" translate="no" truncate="end">
                        {l.skuCode || "-"}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text ff="monospace" fz="var(--nw-fz-mono)">
                        {l.sizeValue || "-"}
                      </Text>
                    </Table.Td>
                    <Table.Td ta="right" ff="monospace" fz="var(--nw-fz-mono)">
                      {l.quantity ?? 0}
                    </Table.Td>
                    <Table.Td ta="right" ff="monospace" fz="var(--nw-fz-mono)" className={classes.noWrap}>
                      {l.weight != null ? kg(l.weight) : "-"}
                    </Table.Td>
                    <Table.Td ta="right" ff="monospace" fz="var(--nw-fz-mono)">
                      {formatMoney(l.price)}
                    </Table.Td>
                    <Table.Td ta="right" ff="monospace" fz="var(--nw-fz-mono)" fw={700}>
                      {formatMoney(l.totalAmount)}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </Box>
      ) : (
        <Text className={classes.hint} ta="center" py="md">
          {t("dnDetail.noItems")}
        </Text>
      )}

      <SimpleGrid cols={{ base: 2, md: 4 }} spacing="sm">
        <Tile label={t("dnDetail.totalPcs")} value={totalItemCount} />
        <Tile label={t("dnDetail.totalWeight")} value={totalWeight > 0 ? kg(Number(totalWeight.toFixed(1))) : "-"} />
        <Tile label={t("dnDetail.effectiveSize")} value={dn.classification?.totalSizeValue ?? "-"} />
        <Tile label={t("dnDetail.orderAmount")} value={formatMoney(dn.totalAmount ?? 0)} />
      </SimpleGrid>
    </Stack>
  )
}
